import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters/number-value-formatter';
import {
  getWaterfallCaseYearInstrumentValue,
  getWaterfallCaseYearInstrumentTrancheValue,
  getWaterfallCaseInstrumentTrancheValue,
  getWaterfallCaseInstrumentValue,
  getWaterfallInstrumentTrancheValue,
  getWaterfallInstrumentValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { PayoutLogicDto } from '@app/shared/models/contracts/project-dto';
import { l } from '@locale/setupI18n';
import { FC, useMemo } from 'react';
import TableSectionValuesPerCase from '../../components/TableSectionValuesPerCase';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import { InstrumentExitProceeds } from './InstrumentExitProceeds';
import {
  getPostDiscountPvLabel,
  ValuationSection,
  getMvLabel,
  getWeightedAverageMvLabel,
} from './utils/valuationLabelHelper';

export const InstrumentValuation: FC<{
  instrument: InstrumentDefinitionWithId;
  tranche: PayoutLogicDto | undefined;
  trancheIndex: number | undefined;
  columnsCount: number;
  hasInterims: boolean;
  hasMultipleTranches: boolean;
  interpolationOption: {
    escapeValue: boolean;
  };
}> = ({
  instrument,
  tranche,
  trancheIndex,
  interpolationOption,
  columnsCount,
  hasInterims,
  hasMultipleTranches,
}) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);

  const shouldDisplayInterimCashflow = hasInterims;
  const maxDataColspanCount = columnsCount - 1;

  const shouldDisplayDloc = useMemo(() => {
    // Check if any case/year has a non-zero dlocPc value for this instrument or its tranches
    if (!results?.waterfall?.cases) {
      return false;
    }

    return Object.values(results.waterfall.cases).some((caseItem) =>
      Object.values(caseItem.years || {}).some((year) => {
        const instrumentData = year.instruments?.[instrument.instrumentId];
        if (!instrumentData) {
          return false;
        }

        // Check instrument-level dlocPc
        if (instrumentData.dlocPc != null && instrumentData.dlocPc !== 0) {
          return true;
        }

        // Check tranche-level dlocPc if tranches exist
        if (tranche && instrumentData.tranches?.[tranche.id]) {
          const trancheData = instrumentData.tranches[tranche.id];
          return trancheData.dlocPc != null && trancheData.dlocPc !== 0;
        }

        return false;
      })
    );
  }, [results, instrument.instrumentId, tranche]);

  return (
    <>
      <InstrumentExitProceeds
        shouldDisplayInterimCashflow={shouldDisplayInterimCashflow}
        tranche={tranche}
        instrument={instrument}
        trancheIndex={trancheIndex}
        columnsCount={columnsCount}
        hasMultipleTranches={hasMultipleTranches}
        interpolationOption={interpolationOption}
      />
      <tr>
        <th className="table-primary__cell--strong">{l('_MinorityDiscounts')}</th>
        <th className="table-primary__cell--strong" colSpan={maxDataColspanCount} />
      </tr>
      {shouldDisplayDloc && (
        <>
          <TableSectionValuesPerForecast
            title={l('_DLOC')}
            renderSpacer={false}
            renderCell={({ forecast, caseItem }) => (
              <CellValue
                {...enteredPercentageViewRules}
                value={
                  hasMultipleTranches
                    ? getCalculatedProjectValueInPercent(
                        getWaterfallCaseYearInstrumentTrancheValue(
                          results,
                          caseItem.caseId,
                          forecast.forecastId,
                          instrument.instrumentId,
                          tranche!.id,
                          (tranche) => tranche.dlocPc
                        )
                      )
                    : getCalculatedProjectValueInPercent(
                        getWaterfallCaseYearInstrumentValue(
                          results,
                          caseItem.caseId,
                          forecast.forecastId,
                          instrument.instrumentId,
                          (instrument) => instrument.dlocPc
                        )
                      )
                }
                dataTestId={`dloc-cell-${caseItem.caseId}-${forecast.forecastId}`}
              />
            )}
          />
        </>
      )}
      <TableSectionValuesPerForecast
        title={l('_DLOM')}
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...enteredPercentageViewRules}
            value={
              hasMultipleTranches
                ? getCalculatedProjectValueInPercent(
                    getWaterfallCaseYearInstrumentTrancheValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrument.instrumentId,
                      tranche!.id,
                      (tranche) => tranche.dlomPc
                    )
                  )
                : getCalculatedProjectValueInPercent(
                    getWaterfallCaseYearInstrumentValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrument.instrumentId,
                      (instrument) => instrument.dlomPc
                    )
                  )
            }
            dataTestId={`dlom-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={getPostDiscountPvLabel({
          section: ValuationSection.ExitProceeds,
          instrument,
          trancheIndex,
          hasInterims: shouldDisplayInterimCashflow,
          interpolationOption,
        })}
        additionalTitleClassNames="table-primary__cell--strong"
        highlight={hasMultipleTranches}
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...amountRules}
            className="table-primary__cell--strong"
            value={
              hasMultipleTranches
                ? getWaterfallCaseYearInstrumentTrancheValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    instrument.instrumentId,
                    tranche!.id,
                    (tranche) => tranche.discountedFairValue
                  )
                : getWaterfallCaseYearInstrumentValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    instrument.instrumentId,
                    (instrument) => instrument.discountedFairValue
                  )
            }
            dataTestId={`post-discount-pv-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerCase
        title={getMvLabel({
          section: ValuationSection.ExitProceeds,
          instrument,
          trancheIndex,
          hasInterims: shouldDisplayInterimCashflow,
          interpolationOption,
        })}
        renderSpacer={false}
        renderCell={({ caseItem }) => {
          return (
            <CellValue
              {...amountRules}
              alignment={Alignment.Center}
              value={
                hasMultipleTranches
                  ? getWaterfallCaseInstrumentTrancheValue(
                      results,
                      caseItem.caseId,
                      instrument.instrumentId,
                      tranche!.id,
                      (tranche) => tranche.weightedDiscountedFairValue
                    )
                  : getWaterfallCaseInstrumentValue(
                      results,
                      caseItem.caseId,
                      instrument.instrumentId,
                      (instrument) => instrument.weightedDiscountedFairValue
                    )
              }
              dataTestId={`mv-of-instrument-cell-${caseItem.caseId}-${instrument.instrumentId}`}
            />
          );
        }}
        additionalTitleClassNames="table-primary__cell--strong"
        additionalCellClassNames="table-primary__cell--strong"
      />
      <tr>
        <th className="table-primary__cell--vertical-separator table-primary__cell--background-secondary table-primary__cell--strong">
          {getWeightedAverageMvLabel({
            section: ValuationSection.ExitProceeds,
            instrument,
            trancheIndex,
            hasInterims: shouldDisplayInterimCashflow,
            interpolationOption,
          })}
        </th>
        <td className="table-primary__cell--vertical-separator table-primary__cell--background-secondary" />
        <td colSpan={columnsCount - 2} className="table-primary__cell--background-secondary">
          <CellValue
            {...amountRules}
            alignment={Alignment.Center}
            strong
            value={
              hasMultipleTranches
                ? getWaterfallInstrumentTrancheValue(
                    results,
                    instrument.instrumentId,
                    tranche!.id,
                    (tranche) => tranche.weightedDiscountedFairValue
                  )
                : getWaterfallInstrumentValue(
                    results,
                    instrument.instrumentId,
                    (instrument) => instrument.weightedExitDiscountedFairValue
                  )
            }
            dataTestId={`weighted-average-market-value-cell-${instrument.instrumentId}`}
          />
        </td>
      </tr>
    </>
  );
};
